/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import './style/checkbox.scss';

import React from 'react';
import {MouseEventHandler} from 'react';
import classNames from 'classnames';
import AwesomeIcon from '../icon/awesomeIcon';
import {findDarkestColor, convertColor, editColor} from "../util/colorUtil";
import {isNotNull, isNull} from "../util/controlUtil";
import {ControlContext} from "../util/controlContext";
import { IconName } from '@fortawesome/fontawesome-svg-core';

interface CheckboxProps {
    onCheck: MouseEventHandler;
    onMouseEnter: any,
    onMouseLeave: any,
    checked: boolean,
    disabled: boolean,
    isHovered: boolean,
    label: string,
    labelPosition: 'left' | 'right',
    containerClass: string,
    labelClass: string,
    checkboxClass: string,
    checkClass: string,
    errorClass: string,
    id: string,
    error: string | null,
    checkIcon: IconName | React.ReactNode
    baseColor?: string;
    contrastColor?: string;
    title?: string;
    useDarkestColor: boolean;
}

export class Checkbox extends React.Component<CheckboxProps> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    getContainerClass = (): string => {
        const {disabled, containerClass, error, label} = this.props, classes = ['checkboxContainer'];

        if (disabled) classes.push('disabled');
        if (isNotNull(error)) classes.push('error');
        if (!label.length) classes.push('no-label');

        classes.push(containerClass);
        return classNames(classes);
    };

    getLabelClass = (position: 'left' | 'right'): string => {
        const {disabled, labelClass, error} = this.props, classes = ['checkboxLabel', position];
        if (disabled) classes.push('disabled');
        if (isNotNull(error)) classes.push('error');
        classes.push(labelClass);
        return classNames(classes);
    };

    getCheckboxClass = (): string => {
        const {disabled, checkboxClass} = this.props, classes = ['checkbox'];
        if (disabled) classes.push('disabled');
        classes.push(checkboxClass);
        return classNames(classes);
    };

    getCheckClass = (): string => {
        const {disabled, checkClass, checked} = this.props, classes = ['checkbox-check'];
        if (disabled) classes.push('disabled');
        if (checked) classes.push('checked');
        classes.push(checkClass);
        return classNames(classes);
    };

    getCheckboxStyle = (): object => {
        const {isHovered, error, disabled, useDarkestColor} = this.props;
        let color: string;
        if (isNotNull(error)) color = 'red';
        else if (disabled) color = 'color-gray-4';
        else {
            if (useDarkestColor)
                color = findDarkestColor(this.getBaseColor(), this.getContrastColor());
            else color = this.getBaseColor();
            if (isHovered) color = editColor(-.2, color);
        }
        return {borderColor: convertColor(color)};
    };

    getCheckStyle = (): object => {
        const {error, disabled, useDarkestColor} = this.props;
        let color: string;
        if (isNotNull(error)) color = 'red';
        else if (disabled) color = 'color-gray-4';
        else if (useDarkestColor) color = findDarkestColor(this.getBaseColor(), this.getContrastColor());
        else color = this.getBaseColor();
        return {color: convertColor(color)};
    };

    getBaseColor = (): string => convertColor(this.props.baseColor || this.context.baseColor);
    getContrastColor = (): string => convertColor(this.props.contrastColor || this.context.contrastColor);

    getMainColor = () => this.props.useDarkestColor ? findDarkestColor(this.getBaseColor(), this.getContrastColor()) : this.getBaseColor();

    renderLabel = (position: 'left' | 'right'): React.ReactNode => {
        const {label, labelPosition} = this.props;
        if (isNull(label) || labelPosition !== position) return null;
        return <label className={this.getLabelClass(position)}>{label}</label>
    };

    renderCheckbox = (): React.ReactNode => {
        return (
            <div className={this.getCheckboxClass()} style={this.getCheckboxStyle()}>
                <div className='ripple' style={{backgroundColor: this.getMainColor()}}/>
                {this.renderCheck()}
            </div>
        )
    };

    renderCheck = () => {
        const {checkIcon} = this.props;
        if (React.isValidElement(checkIcon)) return React.cloneElement((checkIcon as React.ReactElement), {
            className: classNames(checkIcon.props.className, this.getCheckClass()),
            style: {...checkIcon.props.style, ...this.getCheckStyle()}
        });
        const checkName: IconName = typeof checkIcon === 'string' ? checkIcon as IconName : 'check';
        return <AwesomeIcon name={checkName} className={this.getCheckClass()} style={this.getCheckStyle()}/>;
    };

    renderError = (): React.ReactNode => {
        const {error, errorClass} = this.props;
        return <label className={classNames('error', errorClass)}>{error}</label>;
    };

    render = () => {
        const {id, onCheck, onMouseLeave, onMouseEnter, title} = this.props;
        return (
            <div className={this.getContainerClass()} id={id} onClick={onCheck} onMouseEnter={onMouseEnter}
                 title={title} onMouseLeave={onMouseLeave}>
                {this.renderLabel('left')}
                {this.renderCheckbox()}
                {this.renderLabel('right')}
                {this.renderError()}
            </div>
        )
    }
}